// src/shared/components/email/HtmlEditorWithPreview.jsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function HtmlEditorWithPreview({
  mode = "single", // 'single' | 'bulk'
  form,
  errors,
  sending,

  // bulk-only
  recipients = [],
  onRecipientChange,

  // common handlers
  onChange,
  onSubmit,
  onCancel,

  // ✅ NEW: hide sender selector in Advanced (we move it to modal header for bulk)
  hideSenderInAdvanced = false,
}) {
  const { t } = useTranslation();

  const isBulk = mode === "bulk";

  // bottom editor tabs (HTML)
  const [tab, setTab] = useState("edit"); // 'edit' | 'preview'
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [isHtmlFullScreen, setIsHtmlFullScreen] = useState(false);
  const [isPreviewFullScreen, setIsPreviewFullScreen] = useState(false);

  // top recipients tabs (BULK only)
  const [recTab, setRecTab] = useState("edit"); // 'edit' | 'preview'
  const [isRecipientsFullScreen, setIsRecipientsFullScreen] = useState(false);

  const htmlRequired = useMemo(() => {
    const tpl = form?.template;
    return tpl === "no_template" || !tpl;
  }, [form?.template]);

  const bulkHasRecipients = isBulk && Array.isArray(recipients) && recipients.length > 0;

  // Dark styles
  const darkBlockStyle = {
    background: "rgb(31, 34, 48)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "0.5rem",
  };

  const darkControlStyle = {
    backgroundColor: "rgba(0,0,0,0.25)",
    color: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(255,255,255,0.12)",
  };

  const controlStyle = darkControlStyle;

  const titleHtml = t("html_body") || "HTML Body";
  const titlePreview = t("preview") || "Preview";
  const titleRecipients = t("recipients") || "Recipients";
  const titleRecipientsPreview = t("recipients_preview") || "Recipients preview";

  const handleExpand = () => {
    if (tab === "edit") setIsHtmlFullScreen(true);
    else setIsPreviewFullScreen(true);
  };

  const handleRecipientsExpand = () => {
    setIsRecipientsFullScreen(true);
  };

  const RecipientsBlock = ({ fullscreen = false }) => {
    return (
      <div className={fullscreen ? "h-100 d-flex flex-column" : ""} style={fullscreen ? { minHeight: 0 } : undefined}>
        {/* TOP Tabs + Expand */}
        <div className={"d-flex justify-content-between align-items-center " + (fullscreen ? "mb-3" : "mt-3")}>
          <div className="btn-group" role="group" aria-label={t("email_recipients_tabs_aria_plural")}>
            <button
              type="button"
              className={"btn btn-sm " + (recTab === "edit" ? "btn-primary" : "btn-outline-primary")}
              onClick={() => setRecTab("edit")}
            >
              <i class="lni lni-pencil-1"></i> {t("edit") || "Edit"}
            </button>
            <button
              type="button"
              className={"btn btn-sm " + (recTab === "preview" ? "btn-primary" : "btn-outline-primary")}
              onClick={() => setRecTab("preview")}
            >
              <i class="lni lni-eye"></i> {t("preview") || "Preview"}
            </button>
          </div>

          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleRecipientsExpand}>
            ↗ {t("expand") || "Expand"}
          </button>
        </div>

        {/* Content */}
        {recTab === "edit" ? (
          <>
            <label className="form-label mb-1">
              {titleRecipients} <span className="text-danger">*</span>{" "}
              <span className="text-muted small">
                ({bulkHasRecipients ? `${recipients.length}/${recipients.length}` : "0/0"})
              </span>
            </label>

            {bulkHasRecipients ? (
              <div
                style={{
                  ...darkBlockStyle,
                  padding: "0.5rem",
                  maxHeight: fullscreen ? "100%" : "120px",
                  overflowY: "auto",
                  flex: fullscreen ? "1 1 auto" : undefined,
                  minHeight: fullscreen ? 0 : undefined,
                }}
              >
                {recipients.map((r) => (
                  <div
                    key={r.contactId}
                    className="d-flex align-items-center justify-content-between gap-2 mb-2"
                  >
                    <div style={{ color: "rgba(255,255,255,0.85)" }}>
                      {r.full_name || `ID ${r.contactId}`}
                    </div>

                    <div style={{ minWidth: fullscreen ? "360px" : "300px" }}>
                      {Array.isArray(r.emails) && r.emails.length > 0 ? (
                        <select
                          className="form-select form-select-sm"
                          value={r.selected || ""}
                          onChange={(e) => onRecipientChange?.(r.contactId, e.target.value)}
                          style={controlStyle}
                        >
                          {r.emails.map((em) => (
                            <option key={em} value={em}>
                              {em}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-muted small">{t("no_emails") || "No emails"}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted">
                {t("recipients_hint") || "No emails found for selected contacts"}
              </div>
            )}

            <div className="form-text">
              {t("recipients_hint") ||
                "Main emails are selected by default, but you can choose another email per contact."}
            </div>
          </>
        ) : (
          <>
            <label className="form-label mb-1">{titleRecipientsPreview}</label>

            {/* Preview: Recipient Preview */}
            <textarea
              className="form-control"
              value={form?.to_preview || ""}
              readOnly
              style={{
                ...controlStyle,
                fontFamily: "monospace",
                height: fullscreen ? "100%" : "120px",
                resize: "none",
                flex: fullscreen ? "1 1 auto" : undefined,
                minHeight: fullscreen ? 0 : undefined,
              }}
              placeholder={titleRecipientsPreview}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* BULK: Recipients block on top */}
      {isBulk && <RecipientsBlock />}

      {/* TOP FIELDS */}
      <div className="row g-2">
        {!isBulk ? (
          <>
            <div className="col-12 col-md-6">
              <label className="form-label mb-1">
                {t("to") || "To"} <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className={"form-control form-control-sm" + (errors?.to ? " is-invalid" : "")}
                value={form?.to || ""}
                onChange={(e) => onChange?.("to", e.target.value)}
                placeholder={t("email_input_placeholder")}
                style={controlStyle}
              />
              {errors?.to && <div className="invalid-feedback d-block">{errors.to}</div>}
            </div>

            <div className="col-12 col-md-6">
              <label className="form-label mb-1">
                {t("subject") || "Subject"} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={"form-control form-control-sm" + (errors?.subject ? " is-invalid" : "")}
                value={form?.subject || ""}
                onChange={(e) => onChange?.("subject", e.target.value)}
                placeholder={t("subject") || "Subject"}
                style={controlStyle}
              />
              {errors?.subject && <div className="invalid-feedback d-block">{errors.subject}</div>}
            </div>
          </>
        ) : (
          <div className="col-12">
            <label className="form-label mb-1">
              {t("subject") || "Subject"} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={"form-control form-control-sm" + (errors?.subject ? " is-invalid" : "")}
              value={form?.subject || ""}
              onChange={(e) => onChange?.("subject", e.target.value)}
              placeholder={t("subject") || "Subject"}
              style={controlStyle}
            />
            {errors?.subject && <div className="invalid-feedback d-block">{errors.subject}</div>}
          </div>
        )}
      </div>

      {/* ADVANCED */}
      <div className="mt-2">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => setAdvancedOpen((p) => !p)}
          style={{ width: "100%" }}
        >
          {advancedOpen ? (
            <>
              <i className="lni lni-chevron-down"></i> {t("advanced") || "Advanced"}
            </>
          ) : (
            <>
              <i class="lni lni-chevron-left"></i> {t("advanced") || "Advanced"}
            </>
          )}
        </button>

        {advancedOpen && (
          <div className="mt-2 p-2" style={darkBlockStyle}>
            <div className="row g-2">
              {/* sender (hidden when moved to header) */}
              {"sender" in (form || {}) && !hideSenderInAdvanced && (
                <div className="col-12 col-md-6">
                  <label className="form-label mb-1">
                    {t("email_sender") || "Sender"}{" "}
                    <span className="text-muted small">
                      ({t("email_sender_hint_short") || "who the email is sent from"})
                    </span>
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={form?.sender || "auto"}
                    onChange={(e) => onChange?.("sender", e.target.value)}
                    style={controlStyle}
                  >
                    <option value="auto">{t("sender_auto") || "Auto (by settings)"}</option>
                    <option value="company">{t("sender_company") || "Company (corporate)"}</option>
                    <option value="employee">{t("sender_employee") || "Employee (my account)"}</option>
                  </select>
                  <div className="form-text">
                    {t("email_sender_hint") ||
                      "Auto: uses CRM mode (hybrid/corporate/employee). Company: forces corporate sender. Employee: forces your personal SMTP account."}
                  </div>
                </div>
              )}

              {/* template */}
              {"template" in (form || {}) && (
                <div className="col-12 col-md-12">
                  <label className="form-label mb-1">{t("template") || "Template"}</label>
                  <select
                    className="form-select form-select-sm"
                    value={form?.template || "no_template"}
                    onChange={(e) => onChange?.("template", e.target.value)}
                    style={controlStyle}
                  >
                    <option value="no_template">{t("no_template") || "No template"}</option>
                  </select>
                </div>
              )}

              {/* bulk-only extras */}
              {isBulk && (
                <>
                  {"tag" in (form || {}) && (
                    <div className="col-12 col-md-6">
                      <label className="form-label mb-1">{t("campaign_tag") || "Campaign tag"}</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={form?.tag || ""}
                        onChange={(e) => onChange?.("tag", e.target.value)}
                        placeholder="spring_promo_2025"
                        style={controlStyle}
                      />
                    </div>
                  )}

                  {"is_test" in (form || {}) && (
                    <div className="col-12 col-md-6 d-flex align-items-end">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="bulk-is-test"
                          checked={!!form?.is_test}
                          onChange={(e) => onChange?.("is_test", e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="bulk-is-test">
                          {t("is_test_email") || "Test email (limited send)"}
                        </label>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM Tabs + Expand (HTML editor) */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="btn-group" role="group" aria-label={t("email_editor_tabs_aria")}>
          <button
            type="button"
            className={"btn btn-sm " + (tab === "edit" ? "btn-primary" : "btn-outline-primary")}
            onClick={() => setTab("edit")}
          >
            <i class="lni lni-pencil-1"></i> {t("edit") || "Edit"}
          </button>
          <button
            type="button"
            className={"btn btn-sm " + (tab === "preview" ? "btn-primary" : "btn-outline-primary")}
            onClick={() => setTab("preview")}
          >
            <i class="lni lni-eye"></i> {t("preview") || "Preview"}
          </button>
        </div>

        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExpand}>
          ↗ {t("expand") || "Expand"}
        </button>
      </div>

      {/* HTML Edit / Preview */}
      <div className="mt-2">
        {tab === "edit" ? (
          <>
            <label className="form-label mb-1">
              {titleHtml} {htmlRequired && <span className="text-danger">*</span>}
            </label>

            <textarea
              className={"form-control" + (errors?.html_body ? " is-invalid" : "")}
              style={{
                height: "260px",
                fontFamily: "monospace",
                resize: "none",
                ...controlStyle,
              }}
              value={form?.html_body || ""}
              onChange={(e) => onChange?.("html_body", e.target.value)}
              placeholder={t("html_body_hint") || "Write HTML content here"}
            />

            {errors?.html_body && <div className="invalid-feedback d-block">{errors.html_body}</div>}
            <div className="form-text">{t("html_body_hint") || "Write HTML content here"}</div>
          </>
        ) : (
          <div className="border rounded bg-white" style={{ height: "260px", overflow: "hidden" }}>
            {form?.html_body ? (
              <iframe
                title={isBulk ? "bulk-email-preview" : "email-preview"}
                style={{ border: "none", width: "100%", height: "100%" }}
                srcDoc={form.html_body}
              />
            ) : (
              <div className="p-3 text-muted h-100">{titlePreview}</div>
            )}
          </div>
        )}
      </div>

      {/* Footer buttons */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={sending}>
          {t("cancel") || "Cancel"}
        </button>
        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={sending}>
          {sending ? t("sending") || "Sending..." : t("send") || "Send"}
        </button>
      </div>

      {/* FULLSCREEN: Recipients */}
      {isRecipientsFullScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column"
          style={{ zIndex: 1060, backgroundColor: "rgba(0,0,0,0.85)" }}
        >
          <div className="d-flex justify-content-between align-items-center p-3 text-white">
            <div>
              {titleRecipients} / {recTab === "edit" ? (t("edit") || "Edit") : (t("preview") || "Preview")}
            </div>
            <button
              type="button"
              className="btn btn-outline-light btn-sm"
              onClick={() => setIsRecipientsFullScreen(false)}
            >
              <i class="lni lni-xmark"></i>
            </button>
          </div>

          <div className="flex-grow-1 p-3" style={{ minHeight: 0 }}>
            <RecipientsBlock fullscreen />
          </div>
        </div>
      )}

      {/* FULLSCREEN: HTML */}
      {isHtmlFullScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column"
          style={{ zIndex: 1060, backgroundColor: "rgba(0,0,0,0.85)" }}
        >
          <div className="d-flex justify-content-between align-items-center p-3 text-white">
            <div>{titleHtml}</div>
            <button
              type="button"
              className="btn btn-outline-light btn-sm"
              onClick={() => setIsHtmlFullScreen(false)}
            >
              <i class="lni lni-xmark"></i>
            </button>
          </div>
          <div className="flex-grow-1 p-3">
            <textarea
              className="form-control"
              style={{
                width: "100%",
                height: "100%",
                resize: "none",
                fontFamily: "monospace",
              }}
              value={form?.html_body || ""}
              onChange={(e) => onChange?.("html_body", e.target.value)}
            />
          </div>
        </div>
      )}

      {/* FULLSCREEN: Preview */}
      {isPreviewFullScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column"
          style={{ zIndex: 1060, backgroundColor: "rgba(0,0,0,0.85)" }}
        >
          <div className="d-flex justify-content-between align-items-center p-3 text-white">
            <div>{titlePreview}</div>
            <button
              type="button"
              className="btn btn-outline-light btn-sm"
              onClick={() => setIsPreviewFullScreen(false)}
            >
              <i class="lni lni-xmark"></i>
            </button>
          </div>
          <div className="flex-grow-1 p-3">
            <div className="bg-white h-100 w-100 border rounded overflow-hidden" style={{ boxSizing: "border-box" }}>
              {form?.html_body ? (
                <iframe
                  title={isBulk ? "bulk-email-preview-fullscreen" : "email-preview-fullscreen"}
                  style={{ border: "none", width: "100%", height: "100%" }}
                  srcDoc={form.html_body}
                />
              ) : (
                <div className="p-3 text-muted">{titlePreview}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HtmlEditorWithPreview;
